import { ReactNode, useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { GlobalStyles } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { useConfig } from './ConfigProvider';
import '@fontsource/readex-pro/200.css';
import '@fontsource/readex-pro/300.css';
import '@fontsource/readex-pro/400.css';
import '@fontsource/readex-pro/500.css';
import '@fontsource/readex-pro/600.css';
import '@fontsource/readex-pro/700.css';

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { direction } = useConfig();

  useEffect(() => {
    document.dir = direction;
    document.documentElement.lang = direction === 'rtl' ? 'ar' : 'en';
  }, [direction]);

  const theme = useMemo(() => createTheme({
    direction,
    palette: {
      primary: {
        main: '#2563EB',
        light: '#60A5FA',
        dark: '#1D4ED8',
        contrastText: '#fff',
      },
      secondary: {
        main: '#7C3AED',
        light: '#A78BFA',
        dark: '#5B21B6',
      },
      success: {
        main: '#16A34A',
        light: '#4ADE80',
        dark: '#15803D',
      },
      warning: {
        main: '#D97706',
        light: '#FCD34D',
        dark: '#B45309',
      },
      error: {
        main: '#DC2626',
        light: '#FCA5A5',
        dark: '#B91C1C',
      },
      background: {
        default: '#F4F6FA',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#111827',
        secondary: '#6B7280',
      },
      divider: '#E5E7EB',
    },
    typography: {
      fontFamily: 'Readex Pro, sans-serif',
      h4: { fontWeight: 800, letterSpacing: '-0.5px' },
      h5: { fontWeight: 700, letterSpacing: '-0.3px' },
      h6: { fontWeight: 700 },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            fontFamily: 'Readex Pro, sans-serif !important',
            boxSizing: 'border-box',
          },
          body: {
            backgroundColor: '#F4F6FA',
          },
          '::-webkit-scrollbar': {
            width: 6,
            height: 6,
          },
          '::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '::-webkit-scrollbar-thumb': {
            background: '#D1D5DB',
            borderRadius: 3,
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: '#9CA3AF',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            textTransform: 'none',
            fontWeight: 600,
            letterSpacing: 0,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            '&:active': { boxShadow: 'none' },
          },
          contained: {
            background: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1D4ED8 0%, #4338CA 100%)',
            },
          },
          sizeLarge: {
            height: 48,
            fontSize: '0.9375rem',
            borderRadius: 12,
          },
          sizeMedium: {
            height: 40,
          },
          sizeSmall: {
            height: 32,
            fontSize: '0.8125rem',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: '#FAFAFA',
            transition: 'all 0.15s ease',
            '& fieldset': {
              borderColor: '#E5E7EB',
            },
            '&:hover fieldset': {
              borderColor: '#9CA3AF',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2563EB',
              borderWidth: 1.5,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.05)',
            transition: 'box-shadow 0.2s ease',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            fontSize: '0.8125rem',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          elevation1: {
            boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: '#E5E7EB',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 700,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            letterSpacing: 0,
          },
        },
      },
    },
  }), [direction]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          '*': { fontFamily: 'Readex Pro, sans-serif !important' },
          '::selection': { backgroundColor: '#BFDBFE', color: '#1D4ED8' },
          'a': { textDecoration: 'none' },
        }}
      />
      {children}
    </MuiThemeProvider>
  );
};
